/**
 * Timezone helpers — uses `Intl.DateTimeFormat` so there's zero dependency
 * footprint and full ICU timezone support in modern browsers.
 *
 * The trick: any `Date` is just a UTC instant; you can render the same instant
 * in any IANA timezone by asking `Intl.DateTimeFormat({timeZone}).formatToParts`
 * for its wall-clock components.
 */

// Curated short list of common timezones (covers >95% of bookers). Users can
// still type to filter via the <select> if their zone isn't here.
export const COMMON_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'Europe/Amsterdam',
  'Europe/Stockholm',
  'Europe/Athens',
  'Europe/Moscow',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Asia/Dubai',
  'Asia/Karachi',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Seoul',
  'Australia/Perth',
  'Australia/Sydney',
  'Pacific/Auckland',
  'America/Sao_Paulo',
  'America/Argentina/Buenos_Aires',
  'America/Mexico_City',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export function detectBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch (_) {
    return 'UTC';
  }
}

/**
 * Returns wall-clock components for `date` (a JS Date) in the given IANA
 * timezone — year, month (1-12), day (1-31), weekday (0=Mon..6=Sun), hour,
 * minute, second.
 */
export function getWallClock(date, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-GB', {
    timeZone, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    weekday: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = Object.fromEntries(fmt.formatToParts(date).map((p) => [p.type, p.value]));
  const wdMap = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0; // some ICUs emit 24:00 at midnight
  return {
    year:    parseInt(parts.year, 10),
    month:   parseInt(parts.month, 10),
    day:     parseInt(parts.day, 10),
    weekday: wdMap[parts.weekday] ?? 0,
    hour,
    minute:  parseInt(parts.minute, 10),
    second:  parseInt(parts.second, 10),
  };
}

/**
 * Returns the timezone offset (in minutes) at `date` for the given timeZone.
 * Positive = east of UTC. Used for labeling ("UTC+02:00").
 */
export function getOffsetMinutes(date, timeZone) {
  // Find wall-clock in the target zone, treat it as UTC, subtract from real UTC instant.
  const wc = getWallClock(date, timeZone);
  const asUTC = Date.UTC(wc.year, wc.month - 1, wc.day, wc.hour, wc.minute, wc.second);
  return Math.round((asUTC - date.getTime()) / 60000);
}

export function formatOffsetLabel(offsetMinutes) {
  const sign = offsetMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(offsetMinutes);
  return `UTC${sign}${String(Math.floor(abs / 60)).padStart(2, '0')}:${String(abs % 60).padStart(2, '0')}`;
}

/** "Europe/Berlin" -> "Berlin (UTC+02:00)" for display. */
export function formatTimezoneLabel(tz, date = new Date()) {
  const off = formatOffsetLabel(getOffsetMinutes(date, tz));
  const city = tz.includes('/') ? tz.split('/').slice(-1)[0].replace(/_/g, ' ') : tz;
  return `${city} · ${off}`;
}

/** ISO yyyy-mm-dd date key as observed in the given timezone. */
export function toDayKeyInTz(date, timeZone) {
  const wc = getWallClock(date, timeZone);
  const pad = (n) => String(n).padStart(2, '0');
  return `${wc.year}-${pad(wc.month)}-${pad(wc.day)}`;
}

/** Hours+minutes as a fractional hour observed in the given timezone. */
export function hourFractionInTz(date, timeZone) {
  const wc = getWallClock(date, timeZone);
  return wc.hour + wc.minute / 60;
}

/**
 * Builds a JS Date that points to the UTC instant matching the given wall-clock
 * time in the target timezone. Used when the user picks "12:00 Berlin" but we
 * need a real UTC moment to compare against bookings.
 *
 * Iterative correction to handle DST + offset arithmetic — converges in 1-2 iters.
 */
export function wallClockToUTC(year, month, day, hour, minute, timeZone) {
  // First guess: treat the wall clock as UTC.
  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  for (let i = 0; i < 3; i++) {
    const off = getOffsetMinutes(guess, timeZone); // minutes east of UTC
    const desired = Date.UTC(year, month - 1, day, hour, minute) - off * 60000;
    if (Math.abs(desired - guess.getTime()) < 1000) break;
    guess = new Date(desired);
  }
  return guess;
}

/** Format an instant as "HH:MM" in the given timezone. */
export function timeLabelInTz(date, timeZone) {
  const wc = getWallClock(date, timeZone);
  return `${String(wc.hour).padStart(2, '0')}:${String(wc.minute).padStart(2, '0')}`;
}
